import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { DashboardStyleProvider } from './context/DashboardStyleContext';
import LandingPage from './components/landing/LandingPage';
import MyPayslips from './components/payroll/MyPayslips';
import MyLoans from './components/payroll/MyLoans'; // W3-D
import EmployeeLoans from './components/payroll/EmployeeLoans'; // W3-D
import LoanDisbursementBatches from './components/payroll/LoanDisbursementBatches'; // W3-D
import PayrollProvisions from './components/payroll/PayrollProvisions'; // W3-J
import ProvidentFund from './components/payroll/ProvidentFund'; // W3-J
import MyProvidentFund from './components/payroll/MyProvidentFund'; // W3-J
import ProtectedRoute from './components/auth/ProtectedRoute';
import ChangePasswordRequired from './components/auth/ChangePasswordRequired';
import SystemAdminRoute from './components/auth/SystemAdminRoute';
import PermissionRoute from './components/auth/PermissionRoute';
import MenuVisibilityRoute from './components/auth/MenuVisibilityRoute';
import RolesPage from './components/roles/RolesPage';
import RoleEditorPage from './components/roles/RoleEditorPage';
import UserList from './components/users/UserList';
import InstitutionList from './components/institutions/InstitutionList';
import InstitutionDetail from './components/institutions/InstitutionDetail';
import CreateInstitution from './components/institutions/CreateInstitution';
import InstitutionSettingsPage from './components/institutions/InstitutionSettingsPage';
import TenantProvisioning from './components/institutions/TenantProvisioning';
import PlatformUsagePage from './components/institutions/PlatformUsagePage';
import MissingHelpPage from './components/institutions/MissingHelpPage';
import { Box } from '@mui/material';
import Sidebar from './components/shared/Sidebar';
import MobileNavDrawer from './components/shared/MobileNavDrawer';
import { useIsMobile } from './hooks/useIsMobile';
import { NavDrawerContext } from './context/NavDrawerContext';
import { DashboardAccessProvider } from './components/dashboard/useDashboardAccess';
import { PlanProvider } from './context/PlanContext';
import MainHeader from './components/shared/MainHeader';
import SearchPointer from './components/shared/SearchPointer';
import { HelpProvider } from './help/HelpContext';
import { httpHelpLogger } from './help/logger';
import HelpLauncher from './help/HelpLauncher';
import FeedbackLauncher from './components/feedback/FeedbackLauncher';
import MyFeedbackPage from './components/feedback/MyFeedbackPage';
import PlatformFeedbackPage from './components/feedback/PlatformFeedbackPage';
import PlatformAddonsPage from './components/addons/PlatformAddonsPage';
import { AuthService } from './services/auth.service';
import AttendanceList from './components/attendance/AttendanceList';
import EmployeeAttendanceList from './components/attendance/EmployeeAttendanceList';
import InstitutionTodayAttendance from './components/attendance/InstitutionTodayAttendance';
import AttendanceStatusUserList from './components/attendance/AttendanceStatusUserList';
import WorkSitesPage from './components/attendance/WorkSitesPage';
import HolidayList from './components/holiday/HolidayList';
import HolidayForm from './components/holiday/HolidayForm';
import ShiftListPage from './components/shifts/ShiftListPage';
import ShiftAssignmentPage from './components/shifts/ShiftAssignmentPage';
import RotationPatternListPage from './components/shifts/RotationPatternListPage';
import ShiftRosterCalendar from './components/shifts/ShiftRosterCalendar';
import OrphanPunchInbox from './components/shifts/OrphanPunchInbox';
import LeaveApplicationList from './components/leave/LeaveApplicationList';
import AllLeaveApplications from './components/leave/AllLeaveApplications';
import LeavePolicyManagement from './components/leave/LeavePolicyManagement';
import LeaveTypeManagement from './components/leave/LeaveTypeManagement';
import ExpenseCategoryManagement from './components/expense/ExpenseCategoryManagement';
import ExpensePolicyManagement from './components/expense/ExpensePolicyManagement';
// ---- W3-U ----
import ExpensePolicyRates from './components/expense/v2/ExpensePolicyRates';
import CorporateCards from './components/expense/v2/CorporateCards';
import CardStatements from './components/expense/v2/CardStatements';
import MyCardTransactions from './components/expense/v2/MyCardTransactions';
// ---- end W3-U ----
import MyExpenseClaims from './components/expense/MyExpenseClaims';
import ExpenseClaimForm from './components/expense/ExpenseClaimForm';
import ExpenseClaimDetail from './components/expense/ExpenseClaimDetail';
import ExpenseClaimReview from './components/expense/ExpenseClaimReview';
import PendingApprovalExpenseClaims from './components/expense/PendingApprovalExpenseClaims';
import MyAdvances from './components/expense/MyAdvances';
import PendingApprovalAdvances from './components/expense/PendingApprovalAdvances';
import AllExpenseClaims from './components/expense/AllExpenseClaims';
import UserLeaveAllocationManagement from './components/leave/UserLeaveAllocationManagement';
import AllUserLeaveBalances from './components/leave/AllUserLeaveBalances';
import StoreList from './components/store/StoreList';
import ItemCategoryList from './components/item/ItemCategoryList';
import ItemList from './components/item/ItemList';
import StockIn from './components/item/StockIn';
import StockOut from './components/item/StockOut';
import MyRequisitions from './components/requisition/MyRequisitions';
import RequisitionCreate from './components/requisition/RequisitionCreate';
import RequisitionDetails from './components/requisition/RequisitionDetails';
import PendingIssueRequisitions from './components/requisition/PendingIssueRequisitions';
import './App.css';
import PendingIssueRequisitionDetails from "./components/requisition/PendingIssueRequisitionDetails";
import PendingApprovalRequisitions from './components/requisition/PendingApprovalRequisitions';
import PendingApprovalRequisitionDetails from './components/requisition/PendingApprovalRequisitionDetails';
import EmployeeConsumption from './components/report/EmployeeConsumption';
import StatutoryRegistersPage from './components/statutoryRegisters/StatutoryRegistersPage';
import UserCreate from './components/users/UserCreate';
import AllInstitutionUsers from './components/users/AllInstitutionUsers';
import StockList from './components/item/StockList';
import FabricLotRegister from './components/item/FabricLotRegister';
import StockHistory from './components/item/StockHistory';
import LowStockList from './components/item/LowStockList';
import StocktakeList from './components/stocktake/StocktakeList';
import StocktakeCreate from './components/stocktake/StocktakeCreate';
import StocktakeDetails from './components/stocktake/StocktakeDetails';
import StockTransferList from './components/transfer/StockTransferList';
import StockTransferCreate from './components/transfer/StockTransferCreate';
import StockTransferDetails from './components/transfer/StockTransferDetails';
import UnitOfMeasurementList from './components/item/UnitOfMeasurementList';
import AllInstitutionStoreList from "./components/store/AllInstitutionStoreList";
import UserProfile from './components/users/UserProfile';
import ModulesPage from './components/modules/ModulesPage';
import ModuleDetailPage from './components/modules/ModuleDetailPage';
import ModuleBreadcrumbs from './components/modules/ModuleBreadcrumbs';
import { PERMISSIONS } from './constants/permissions';
import { UserType } from './interfaces/userType.enum';
import ChartOfAccounts from './components/accounting/ChartOfAccounts';
import JournalEntryList from './components/accounting/JournalEntryList';
import JournalEntryCreate from './components/accounting/JournalEntryCreate';
import ContraVoucherCreate from './components/accounting/ContraVoucherCreate';
import JournalEntryDetail from './components/accounting/JournalEntryDetail';
import RecurringJournalList from './components/accounting/RecurringJournalList';
import RecurringJournalForm from './components/accounting/RecurringJournalForm';
import RecurringJournalDetail from './components/accounting/RecurringJournalDetail';
import GroupAccountBalances from './components/accounting/GroupAccountBalances';
import OpeningBalances from './components/accounting/OpeningBalances';
import TransactionList from './components/accounting/TransactionList';
import BudgetList from './components/accounting/BudgetList';
import AccountLedger from './components/accounting/AccountLedger';
import TrialBalance from './components/accounting/TrialBalance';
import SegmentList from './components/accounting/SegmentList';
import SegmentSettings from './components/accounting/SegmentSettings';
// W3-C inter-segment loans
import InterSegmentLoans from './components/accounting/InterSegmentLoans';
import InterSegmentSettings from './components/accounting/InterSegmentSettings';
import InterSegmentReconciliation from './components/accounting/InterSegmentReconciliation';
import InterSegmentElimination from './components/accounting/InterSegmentElimination';
import LedgerCloseSettings from './components/accounting/LedgerCloseSettings';
import ExchangeRates from './components/accounting/ExchangeRates';
import FxRevaluation from './components/accounting/FxRevaluation';
import CorporateTaxPage from './components/accounting/corporatetax/CorporateTaxPage'; // W3-G
// ---- W3-V FP&A ----
import BudgetVersions from './components/accounting/fpa/BudgetVersions';
import BudgetVarianceReview from './components/accounting/fpa/BudgetVarianceReview';
import KpiPack from './components/accounting/fpa/KpiPack';
import BoardPackPage from './components/accounting/fpa/BoardPackPage';
import BuyerProfitabilityPage from './components/accounting/fpa/BuyerProfitabilityPage';
// ---- end W3-V ----
// ---- W3-X data in/out ----
import DataImport from './components/dataexchange/DataImport';
import Integrations from './components/dataexchange/Integrations';
import XeroCallbackPage from './components/dataexchange/XeroCallbackPage';
// ---- end W3-X ----
// ---- W3-M inter-company ----
import Intercompany from './components/accounting/intercompany/Intercompany';
import GroupConsolidation from './components/accounting/intercompany/GroupConsolidation';
import InstitutionGroups from './components/saas/InstitutionGroups';
// ---- end W3-M ----
import AuditLog from './components/accounting/AuditLog'; // W3-K
// W3-A financing
import FacilityList from './components/financing/FacilityList';
import FacilityDetail from './components/financing/FacilityDetail';
import InterestRuns from './components/financing/InterestRuns';
import BorrowingReports from './components/financing/BorrowingReports';
import OrderCosts from './components/costing/OrderCosts';
import OrderCostDetail from './components/costing/OrderCostDetail';
import ProductionOutputPage from './components/costing/ProductionOutputPage';
import AbsorptionRuns from './components/costing/AbsorptionRuns';
import CostingSettingsPage from './components/costing/CostingSettingsPage';
import Covenants from './components/financing/Covenants';
import ExportFinance from './components/financing/ExportFinance'; // W3-B
import YearEndClose from './components/accounting/YearEndClose';
// W3-R
import StatutoryRemittances from './components/compliance/StatutoryRemittances';
import CloseChecklist from './components/compliance/CloseChecklist';
import ComplianceCalendar from './components/compliance/ComplianceCalendar';
import AllocationRuleList from './components/accounting/AllocationRuleList';
import SegmentTrialBalance from './components/accounting/SegmentTrialBalance';
import SegmentIncomeStatement from './components/accounting/SegmentIncomeStatement';
import SegmentBalanceSheet from './components/accounting/SegmentBalanceSheet';
import CashFlowStatement from './components/accounting/CashFlowStatement';
// ---- W3-K statements ----
import EquityStatement from './components/accounting/EquityStatement';
import NotesPack from './components/accounting/NotesPack';
// ---- end W3-K statements ----
// ---- W3-K journal-import/tally ----
import JournalImport from './components/accounting/JournalImport';
import TallyExport from './components/accounting/TallyExport';
// ---- end W3-K journal-import/tally ----
import DayBook from './components/accounting/DayBook';
import CashFlowClassification from './components/accounting/CashFlowClassification';
import BankAccountList from './components/bank/BankAccountList';
import BankAccountForm from './components/bank/BankAccountForm';
import BankReconciliationList from './components/bank/BankReconciliationList';
import BankReconciliationWorkspace from './components/bank/BankReconciliationWorkspace';
import UnreconciledReport from './components/bank/UnreconciledReport';
// W3-H
import ChequeRegister from './components/bank/ChequeRegister';
// W3-L
import CashForecast from './components/cashforecast/CashForecast';
// ---- B4-B ----
import FcConversions from './components/bank/FcConversions';
// ---- end B4-B ----
// ---- FW-4A ----
import BankRules from './components/bank/BankRules';
import CashbookAccess from './components/bank/CashbookAccess';   // F11 (11b)
import TxnMappingRules from './components/accounting/TxnMappingRules';   // F11 (11c)
// ---- FW-3A ----
import ApprovalsInbox from './components/approvals/ApprovalsInbox';
import ApprovalWorkflows from './components/approvals/ApprovalWorkflows';
// ---- end FW-3A ----
// ---- end FW-4A ----
import ChequeBooks from './components/bank/ChequeBooks';
import ChequeLayouts from './components/bank/ChequeLayouts';
import BankGuarantees from './components/bank/BankGuarantees';
// W3-Q treasury desk
import CashPosition from './components/bank/CashPosition';
import BankTransfers from './components/bank/BankTransfers';
import Fdrs from './components/bank/Fdrs';
import PettyCash from './components/bank/PettyCash';
import TreasurySettings from './components/bank/TreasurySettings';
import MyPettyCash from './components/bank/MyPettyCash';
import FixedAssetList from './components/fixedAsset/FixedAssetList';
import FixedAssetForm from './components/fixedAsset/FixedAssetForm';
import FixedAssetDetail from './components/fixedAsset/FixedAssetDetail';
import FixedAssetCounts from './components/fixedAsset/FixedAssetCounts'; // Q1
import FixedAssetCountDetail from './components/fixedAsset/FixedAssetCountDetail'; // Q1
import QrLanding from './scan/QrLanding'; // Q1
import DepreciationRun from './components/fixedAsset/DepreciationRun';
import AssetDepreciationSchedule from './components/fixedAsset/AssetDepreciationSchedule';
import TaxCodesPage from './components/accounting/TaxCodesPage';
import VatReturnPage from './components/accounting/VatReturnPage';
import MushakRegisterPage from './components/accounting/MushakRegisterPage';
import Mushak91Page from './components/accounting/Mushak91Page';
// ---- B4-A ----
import Mushak610Page from './components/accounting/Mushak610Page';
import MushakCoefficientPage from './components/accounting/MushakCoefficientPage';
// ---- end B4-A ----
// ---- B4-G ----
import SupplierStatementReconPage from './components/procurement/SupplierStatementReconPage';
// ---- end B4-G ----
// ---- B4-F ----
import PoApprovalBands from './components/procurement/approval/PoApprovalBands';
// ---- end B4-F ----
// ---- B4-H ----
import DepartmentPlPage from './components/costcentre/DepartmentPlPage';
// ---- end B4-H ----
// W3-S
import NotificationsPage from './components/notifications/NotificationsPage';
import NotificationPreferencesPage from './components/notifications/NotificationPreferencesPage';
import ReminderRulesPage from './components/notifications/ReminderRulesPage';
import FixedAssetSettings from './components/fixedAsset/FixedAssetSettings';
import AssetRegisterReport from './components/fixedAsset/AssetRegisterReport';
import DepreciationScheduleReport from './components/fixedAsset/DepreciationScheduleReport';
import FixedAssetTieOutReport from './components/fixedAsset/FixedAssetTieOutReport';
// ---- W3-K asset categories and CWIP ----
import FixedAssetCategories from './components/fixedAsset/FixedAssetCategories';
import CwipProjects from './components/fixedAsset/CwipProjects';
// ---- end W3-K asset categories and CWIP ----
// ---- F10 asset locations ----
import FixedAssetLocations from './components/fixedAsset/FixedAssetLocations';
// ---- end F10 ----
import InventoryLedgerTieOut from './components/item/InventoryLedgerTieOut';
import Dashboard from './components/dashboard/Dashboard';
import ModuleBoard from './components/dashboard/board/ModuleBoard';
import KpiTargetsSettings from './components/dashboard/pages/KpiTargetsSettings';
import RoleDefaultsPage from './components/dashboard/managed/RoleDefaultsPage';
import TeamDashboardsPage from './components/dashboard/managed/TeamDashboardsPage';
import PersonBoardPage from './components/dashboard/managed/PersonBoardPage';
import { MODULE_DEFAULT_PRESETS } from './components/dashboard/registry/presets';
import DeviceUserList from './components/device-user/DeviceUserList';
import DeviceApiKeyList from './components/device-api-key/DeviceApiKeyList';
import SalaryComponentList from './components/payroll/SalaryComponentList';
import WageGradeManagement from './components/payroll/WageGradeManagement';
import AnnualIncrements from './components/payroll/AnnualIncrements';
import EmployeeSalaryList from './components/payroll/EmployeeSalaryList';
import PayrollSettings from './components/payroll/PayrollSettings';
import InsurancePolicyPanel from './components/insurance/InsurancePolicyPanel';
import CommercialPartyList from './components/commercial/CommercialPartyList';
import ExportLcList from './components/commercial/ExportLcList';
import ExportLcForm from './components/commercial/ExportLcForm';
import EquipmentList from './components/maintenance/EquipmentList';
import EquipmentForm from './components/maintenance/EquipmentForm';
import EquipmentDetail from './components/maintenance/EquipmentDetail';
import EquipmentImport from './components/maintenance/EquipmentImport';
import MaintenanceSettings from './components/maintenance/MaintenanceSettings';
import UtilityLogSheet from './components/maintenance/UtilityLogSheet';
import MeterReadingHistory from './components/maintenance/MeterReadingHistory';
import BreakdownRequestInbox from './components/maintenance/BreakdownRequestInbox';
import WorkOrderList from './components/maintenance/WorkOrderList';
import WorkOrderDetail from './components/maintenance/WorkOrderDetail';
import PmPlanList from './components/maintenance/PmPlanList';
import PmPlanForm from './components/maintenance/PmPlanForm';
import PmDueList from './components/maintenance/PmDueList';
import MaintenanceReports from './components/maintenance/reports/MaintenanceReports';
import MyBreakdownSection from './components/maintenance/MyBreakdownSection';
import MaintenanceDashboard from './components/dashboard/MaintenanceDashboard';
import TechnicianDashboard from './components/dashboard/TechnicianDashboard';
import ExportLcDetail from './components/commercial/ExportLcDetail';
import BackToBackLcRegister from './components/commercial/BackToBackLcRegister';
import B2bLcDetail from './components/commercial/B2bLcDetail';
import B2bMaturityScheduleReport from './components/commercial/reports/B2bMaturitySchedule';
import UdRegisterList from './components/commercial/UdRegisterList';
import UdRegisterForm from './components/commercial/UdRegisterForm';
import UdRegisterDetail from './components/commercial/UdRegisterDetail';
import ExpFormList from './components/commercial/ExpFormList';
import ExpFormForm from './components/commercial/ExpFormForm';
import CashIncentiveClaimList from './components/commercial/CashIncentiveClaimList';
import CashIncentiveClaimForm from './components/commercial/CashIncentiveClaimForm';
import CommercialSettingsPage from './components/commercial/CommercialSettingsPage';
import CommercialDashboard from './components/commercial/CommercialDashboard';
import LcPositionReport from './components/commercial/reports/LcPositionReport';
import UnrealizedExpAgingReport from './components/commercial/reports/UnrealizedExpAgingReport';
import ExportReceivablesReport from './components/commercial/reports/ExportReceivablesReport';
import IncentiveClaimStatusReport from './components/commercial/reports/IncentiveClaimStatusReport';
import BondLicenseList from './components/commercial/BondLicenseList';
import BondRegisterReport from './components/commercial/reports/BondRegisterReport';
// W3-W
import UdReconciliation from './components/commercial/UdReconciliation';
import OverdueExpBills from './components/commercial/OverdueExpBills';
import RealisationPerformanceReport from './components/commercial/reports/RealisationPerformanceReport';
import OrderProfitabilityReport from './components/commercial/reports/OrderProfitabilityReport';
import PackingListList from './components/commercial/PackingListList';
import PackingListDetail from './components/commercial/PackingListDetail';
import PackingListForm from './components/commercial/PackingListForm';
import BillOfLadingList from './components/commercial/BillOfLadingList';
import BillOfLadingDetail from './components/commercial/BillOfLadingDetail';
import BillOfLadingForm from './components/commercial/BillOfLadingForm';
import ExportInvoiceList from './components/commercial/ExportInvoiceList';
import ExportInvoiceDetail from './components/commercial/ExportInvoiceDetail';
import ExportInvoiceForm from './components/commercial/ExportInvoiceForm';
import PayrollList from './components/payroll/PayrollList';
import PayrollDetail from './components/payroll/PayrollDetail';
import PaymentReconciliation from './components/payroll/PaymentReconciliation';
import PaymentBatchList from './components/payroll/PaymentBatchList';
import FestivalBonusList from './components/payroll/FestivalBonusList';
import LeaveEncashmentList from './components/payroll/LeaveEncashmentList';
import EmployeeExitList from './components/payroll/EmployeeExitList';
import PieceRateOperationList from './components/pieceRate/PieceRateOperationList';
import ProductionEntryList from './components/pieceRate/ProductionEntryList';
import ImpersonationBanner from './components/shared/ImpersonationBanner';
import EmployeeDocumentAdmin from './components/users/EmployeeDocumentAdmin';
import DepartmentList from './components/department/DepartmentList';
import DepartmentForm from './components/department/DepartmentForm';
import DepartmentUserAssignment from './components/department/DepartmentUserAssignment';
import NoticeFeed from './components/notice/NoticeFeed';
import NoticeAdminList from './components/notice/NoticeAdminList';
import MyTasks from './components/task/MyTasks';
import AssetRequisitions from './components/fixedAsset/requisition/AssetRequisitions'; // F8
import BillRequestInbox from './components/procurement/billrequests/BillRequestInbox'; // F8
import RepairBillQueue from './components/procurement/repairbills/RepairBillQueue';
import TasksAssignedByMe from './components/task/TasksAssignedByMe';
// ---- P1-B portal ----
import PortalRoot from './portal/PortalRoot';
import PortalSettings from './components/sales/portal/PortalSettings';
// ---- end P1-B ----
// ---- P1-A projects ----
import ProjectList from './components/project/ProjectList';
import ProjectDetail from './components/project/ProjectDetail';
import ProjectPlPage from './components/project/ProjectPlPage';
import MyProjects from './components/project/MyProjects';
// ---- end P1-A ----
import TaskAdminList from './components/task/TaskAdminList';
import TaxSettings from './components/tax/TaxSettings';
import TaxSlabList from './components/tax/TaxSlabList';
import TaxExemptionList from './components/tax/TaxExemptionList';
import EmployeeTaxList from './components/tax/EmployeeTaxList';
import TaxReports from './components/tax/TaxReports';
import { SupplierList } from './components/procurement/SupplierList';
import { SupplierForm } from './components/procurement/SupplierForm';
import { SupplierDetail } from './components/procurement/SupplierDetail';
import { SupplierCatalogueBrowse } from './components/procurement/SupplierCatalogueBrowse';
import { ProcurementSettings } from './components/procurement/ProcurementSettings';
import { PurchaseOrderList } from './components/procurement/PurchaseOrderList';
import { PurchaseOrderCreate } from './components/procurement/PurchaseOrderCreate';
import { PendingApprovalPurchaseOrders } from './components/procurement/PendingApprovalPurchaseOrders';
import { PendingApprovalPurchaseOrderDetail } from './components/procurement/PendingApprovalPurchaseOrderDetail';
import { PurchaseOrderDetail } from './components/procurement/PurchaseOrderDetail';
import { GoodsReceiptList } from './components/procurement/GoodsReceiptList';
import { GoodsReceiptCreate } from './components/procurement/GoodsReceiptCreate';
import { GoodsReceiptDetail } from './components/procurement/GoodsReceiptDetail';
// ---- W3-F import landed cost ----
// ---- FW-2C ----
import RecurringDocumentList from './components/recurring/RecurringDocumentList';
import RecurringDocumentDetail from './components/recurring/RecurringDocumentDetail';
// ---- end FW-2C ----
import { SupplierServiceBillCreate } from './components/procurement/landedcost/SupplierServiceBillCreate';
import { BillOfEntryList } from './components/procurement/landedcost/BillOfEntryList';
import { BillOfEntryWizard } from './components/procurement/landedcost/BillOfEntryWizard';
import { BillOfEntryDetail } from './components/procurement/landedcost/BillOfEntryDetail';
// ---- end W3-F ----
import { SupplierBillList } from './components/procurement/SupplierBillList';
import { SupplierBillCreate } from './components/procurement/SupplierBillCreate';
import { SupplierBillDetail } from './components/procurement/SupplierBillDetail';
import { SupplierBillEdit } from './components/procurement/SupplierBillEdit';
// ---- W3-E: credit notes, debit notes, supplier advances
import { CreditNoteList } from './components/sales/CreditNoteList';
import { CreditNoteCreate } from './components/sales/CreditNoteCreate';
import CreditNoteDetail from './components/sales/CreditNoteDetail';
import { DebitNoteList } from './components/procurement/DebitNoteList';
import { DebitNoteCreate } from './components/procurement/DebitNoteCreate';
import DebitNoteDetail from './components/procurement/DebitNoteDetail';
import { SupplierAdvanceList } from './components/procurement/SupplierAdvanceList';
import { SupplierPaymentList } from './components/procurement/SupplierPaymentList';
import { SupplierPaymentCreate } from './components/procurement/SupplierPaymentCreate';
import { SupplierPaymentDetail } from './components/procurement/SupplierPaymentDetail';
import { SupplierPaymentEdit } from './components/procurement/SupplierPaymentEdit';
import { WithholdingSettingsPage } from './components/procurement/withholding/WithholdingSettingsPage';
import { WithholdingRegister } from './components/procurement/withholding/WithholdingRegister';
import { WithholdingDeposits } from './components/procurement/withholding/WithholdingDeposits';
// W3-P payment runs
import { PaymentRunList } from './components/procurement/paymentrun/PaymentRunList';
import { PaymentRunDetail } from './components/procurement/paymentrun/PaymentRunDetail';
import { BlockedBills } from './components/procurement/paymentrun/BlockedBills';
import { PaymentSettings } from './components/procurement/paymentrun/PaymentSettings';
import CustomerList from './components/sales/CustomerList';
import CustomerForm from './components/sales/CustomerForm';
import CustomerDetail from './components/sales/CustomerDetail';
import SalesSettings from './components/sales/SalesSettings';
import InvoiceBrandingSettings from './components/sales/InvoiceBrandingSettings';
import EmailTemplatesSettings from './components/sales/EmailTemplatesSettings'; // F7
import SalesInvoiceList from './components/sales/SalesInvoiceList';
import SalesInvoiceCreate from './components/sales/SalesInvoiceCreate';
import SalesInvoiceDetail from './components/sales/SalesInvoiceDetail';
import SalesInvoiceEdit from './components/sales/SalesInvoiceEdit';
import ReceiptDetail from './components/sales/ReceiptDetail';
import QuotationList from './components/sales/QuotationList';
import SalesCatalogue from './components/sales/SalesCatalogue';   // FW-2A
import CashSaleList from './components/sales/CashSaleList';   // FW-2B
import CashSaleForm from './components/sales/CashSaleForm';   // FW-2B
import QuotationCreate from './components/sales/QuotationCreate';
import QuotationDetail from './components/sales/QuotationDetail';
// ---- W3-O ----
import SalesOrderList from './components/sales/SalesOrderList';
import SalesTeams from './components/sales/SalesTeams';
import SalesTargets from './components/sales/SalesTargets';
import SalesOrderForm from './components/sales/SalesOrderForm';
import SalesOrderDetail from './components/sales/SalesOrderDetail';
import DeliveryChallanList from './components/sales/DeliveryChallanList';
import DeliveryChallanDetail from './components/sales/DeliveryChallanDetail';
import ShipmentList from './components/sales/fulfilment/ShipmentList'; // F5
// ---- B4-D ----
import SalesReturnList from './components/sales/SalesReturnList';
import SalesReturnForm from './components/sales/SalesReturnForm';
import SalesReturnDetail from './components/sales/SalesReturnDetail';
import StockPeriodReport from './components/item/StockPeriodReport';
// ---- end B4-D ----
// ---- F6a lot expiry ----
import ExpiryReport from './components/item/ExpiryReport';
import InventorySettingsPage from './components/item/InventorySettings';
// ---- end F6a ----
// ---- F6b serial numbers ----
import SerialLookup from './components/serial/SerialLookup';
import ItemCustodyList from './components/item/ItemCustodyList';
import StockReservationList from './components/item/StockReservationList';
// ---- end F6b ----
import BackOrders from './components/sales/BackOrders';
// ---- end W3-O ----
import ArAgingReport from './components/sales/ArAgingReport';
import SupplierPayablesReport from './components/procurement/SupplierPayablesReport';
import CustomerStatement from './components/sales/CustomerStatement';
import CollectionsWorklist from './components/sales/CollectionsWorklist';
import CustomerRemittances from './components/sales/CustomerRemittances';
import DunningLetters from './components/sales/DunningLetters';
import PaymentLinks from './components/sales/PaymentLinks';
import GatewayPayouts from './components/sales/GatewayPayouts';
import PaymentGatewaySettings from './components/sales/PaymentGatewaySettings';
import { PublicPayPage, PublicPayResultPage } from './components/sales/PublicPayPage';
import ReceiptVerifyPage from './components/sales/ReceiptVerifyPage';
import LeadList from './components/crm/LeadList';
import LeadDetail from './components/crm/LeadDetail';
import PipelineBoard from './components/crm/PipelineBoard';
import OpportunityDetail from './components/crm/OpportunityDetail';
import BuyerList from './components/merchandising/BuyerList';
import StyleList from './components/merchandising/StyleList';
import StyleForm from './components/merchandising/StyleForm';
import SampleRequestList from './components/merchandising/SampleRequestList';
import BuyerOrderList from './components/merchandising/BuyerOrderList';
import BuyerOrderForm from './components/merchandising/BuyerOrderForm';
import OrderJourneyPage from './components/merchandising/OrderJourneyPage';
import OrderBookReport from './components/merchandising/OrderBookReport';
import SampleTrackerReport from './components/merchandising/SampleTrackerReport';
import OrderDashboard from './components/merchandising/OrderDashboard';
import MerchandisingDashboard from './components/merchandising/MerchandisingDashboard';
import SampleWorkloadReport from './components/merchandising/SampleWorkloadReport';
import QcInspectionList from './components/merchandising/QcInspectionList';
import AgentCommissionPage from './components/commercial/AgentCommissionPage';
import BillingPage from './components/billing/BillingPage';
import RoleLanding from './components/auth/RoleLanding';
import { LANDING_ROUTE, safeReturnPath } from './utils/roleLanding';
import PageNotFound from './components/auth/PageNotFound';
import OperatorBillingPage from './components/billing/OperatorBillingPage';

// Component to handle login route logic
const LoginRoute = () => {
  const location = useLocation();
  // Signed-in users land on the dashboard for their role (/home resolves it).
  const from = safeReturnPath(location.state?.from)
    ?? safeReturnPath(new URLSearchParams(location.search).get('from'))
    ?? LANDING_ROUTE;

  if (AuthService.isValidToken()) {
    return <Navigate to={from} replace />;
  }

  return <LandingPage />;
};

interface AppShellProps {
  children: React.ReactNode;
  drawerOpen: boolean;
  onCloseDrawer: () => void;
}

/**
 * The signed-in layout: sidebar (or, below `md`, a temporary Drawer) plus the routed content.
 *
 * Its own component, not inlined in `App`, because `useIsMobile` has to run inside the router
 * tree rather than in `App` itself.
 */
const AppShell = ({ children, drawerOpen, onCloseDrawer }: AppShellProps) => {
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  // The drawer must not survive a viewport widening past `md` — otherwise a phone-width open,
  // then a resize/rotate back to desktop width, then narrowing again would find it already open.
  useEffect(() => {
    if (!isMobile && drawerOpen) {
      onCloseDrawer();
    }
  }, [isMobile, drawerOpen, onCloseDrawer]);

  return (
    <div className="app-container">
      <ImpersonationBanner />
      {isMobile ? (
        <MobileNavDrawer open={drawerOpen} onClose={onCloseDrawer} />
      ) : (
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(prev => !prev)} />
      )}
      <Box
        className="content-container"
        sx={{
          // Below md the drawer floats above the page, so content claims the full width.
          marginLeft: isMobile ? 0 : (sidebarCollapsed ? '60px' : '250px'),
          width: isMobile ? '100%' : (sidebarCollapsed ? 'calc(100% - 60px)' : 'calc(100% - 250px)'),
          minWidth: 0, // lets flex children shrink instead of forcing horizontal page scroll
        }}
      >
        <main className="main-content">{children}</main>
      </Box>
      <FeedbackLauncher />
      <HelpLauncher />
    </div>
  );
};

function App() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  // Wrapped in useMemo so the provider value is not a fresh object on every App render.
  const navDrawerContextValue = useMemo(() => ({ openDrawer: () => setDrawerOpen(true) }), []);

  return (
    <AuthProvider>
      <ToastProvider>
      <DashboardStyleProvider>
      <Router>
        <NavDrawerContext.Provider value={navDrawerContextValue}>
        <div className="app">
          <MainHeader />
          <SearchPointer />
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/change-password" element={<ChangePasswordRequired />} />
            {/* W3-T public pay pages: no login, the link token is the capability */}
            <Route path="/pay/result/:ref" element={<PublicPayResultPage />} />
            <Route path="/pay/:token" element={<PublicPayPage />} />
            <Route path="/verify/receipt/:id" element={<ReceiptVerifyPage />} />
            {/* ---- P1-B customer portal: own session, no staff login ---- */}
            <Route path="/portal/*" element={<PortalRoot />} />
            {/* ---- end P1-B ---- */}

            {/* Protected Routes with Sidebar */}
            <Route element={<ProtectedRoute />}>
              <Route
                path="/*"
                element={
                  <PlanProvider>
                  <DashboardAccessProvider>
                    <HelpProvider logger={httpHelpLogger}>
                    <AppShell drawerOpen={drawerOpen} onCloseDrawer={closeDrawer}>
                      <ModuleBreadcrumbs />
                        <Routes>
                          {/* Institution Admin Only Routes */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.USER_VIEW} />}>
                            <Route path="/users" element={<UserList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.USER_MANAGE} />}>
                            <Route path="/users/create" element={<UserCreate />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EMPLOYEE_DOCUMENT_VIEW} />}>
                            <Route path="/employee-documents" element={<EmployeeDocumentAdmin />} />
                          </Route>
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.HOLIDAY_MANAGE, PERMISSIONS.HOLIDAY_UPDATE]} />}>
                            <Route path="/holidays/create" element={<HolidayForm />} />
                            <Route path="/holidays/edit/:id" element={<HolidayForm />} />
                          </Route>
                          {/* ---- B4-D stock period report ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_VIEW} showNoAccess />}>
                            <Route path="/inventory/reports/stock-period" element={<StockPeriodReport />} />
                          </Route>
                          {/* ---- end B4-D ---- */}
                          {/* ---- F6a lot expiry report ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_VIEW} showNoAccess />}>
                            <Route path="/inventory/reports/expiry" element={<ExpiryReport />} />
                          </Route>
                          {/* ---- G6a inventory costing settings (read STOCK_VIEW; switch INVENTORY_SETTINGS_MANAGE) ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_VIEW} showNoAccess />}>
                            <Route path="/inventory/settings" element={<InventorySettingsPage />} />
                          </Route>
                          {/* ---- end F6a ---- */}
                          {/* ---- F6b serial lookup ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_VIEW} showNoAccess />}>
                            <Route path="/inventory/serials" element={<SerialLookup />} />
                            <Route path="/inventory/serials/:id" element={<SerialLookup />} />
                          </Route>
                          {/* ---- end F6b ---- */}
                          {/* ---- returnable items out with people (STOCK_VIEW; return / write-off need STOCK_ISSUE) ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_VIEW} showNoAccess />}>
                            <Route path="/inventory/custody" element={<ItemCustodyList />} />
                          </Route>
                          {/* ---- buying-house D1: stock held for buyer orders (STOCK_RESERVATION_VIEW; release needs _MANAGE) ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_RESERVATION_VIEW} showNoAccess />}>
                            <Route path="/inventory/reservations" element={<StockReservationList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STORE_VIEW} />}>
                            <Route path="/stores" element={<StoreList />} />
                          </Route>
                          {/* BASE was platformRoles={[InstitutionAdmin]} inside the admin-only bundle;
                              STOCK_VIEW (IA, StoreAdmin, StoreManager, StoreStaff) would newly expose
                              this to store roles, so it stays admin-gated directly. */}
                          <Route element={<PermissionRoute adminOnly />}>
                            <Route path="/reports/employee-consumption" element={<EmployeeConsumption />} />
                          </Route>
                          {/* Statutory registers bundle (spec §2, H6): deliberately NOT wrapped in a
                              PermissionRoute — the page itself gates each of its four tabs on its own
                              key and renders a named explanation when the caller holds none of them,
                              so this route must stay reachable regardless of permissions. */}
                          <Route path="/reports/statutory-registers" element={<StatutoryRegistersPage />} />
                          <Route element={<PermissionRoute adminOnly />}>
                            <Route path="/roles" element={<RolesPage />} />
                            <Route path="/roles/new" element={<RoleEditorPage />} />
                            <Route path="/roles/:roleId" element={<RoleEditorPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.ATTENDANCE_VIEW_ALL} />}>
                            <Route path="/attendance/employee" element={<EmployeeAttendanceList />} />
                            <Route path="/attendance/today" element={<InstitutionTodayAttendance />} />
                            <Route path="/attendance/status/:status" element={<AttendanceStatusUserList />} />
                          </Route>
                          {/* Work sites (geofence config) — keyed to match the backend
                              WorkSiteController re-key: ATTENDANCE_MANAGE gates every write on
                              that controller, and this page is a management screen. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.ATTENDANCE_MANAGE} />}>
                            <Route path="/attendance/sites" element={<WorkSitesPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.DEVICE_MANAGE} />}>
                            <Route path="/device-users" element={<DeviceUserList />} />
                            <Route path="/device-api-keys" element={<DeviceApiKeyList />} />
                          </Route>

                          {/* Department Routes — list + user assignment (InstitutionAdmin OR department admin) */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.DEPARTMENT_MANAGE} alsoAllowIf={u => u?.isDepartmentAdmin === true} />}>
                            <Route path="/departments" element={<DepartmentList />} />
                            <Route path="/departments/:departmentId/users" element={<DepartmentUserAssignment />} />
                          </Route>

                          {/* Department CRUD — InstitutionAdmin only */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.DEPARTMENT_MANAGE} />}>
                            <Route path="/departments/create" element={<DepartmentForm />} />
                            <Route path="/departments/edit/:id" element={<DepartmentForm />} />
                          </Route>

                          {/* Billing — InstitutionAdmin only */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.BILLING_VIEW} />}>
                            <Route path="/billing" element={<BillingPage />} />
                          </Route>

                          {/* Operator Billing console — SaaSOperator + SystemAdmin */}
                          <Route element={<PermissionRoute platformRoles={[UserType.SystemAdmin, UserType.SaaSOperator]} />}>
                            <Route path="/operator/billing" element={<OperatorBillingPage />} />
                          </Route>

                          {/* Leave sub-menu routes — each guarded by its own sub-menu permission */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_ALL_APPLICATIONS} />}>
                            <Route path="/all-leave-applications" element={<AllLeaveApplications />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_TYPES} />}>
                            <Route path="/leave-type" element={<LeaveTypeManagement />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_POLICY} />}>
                            <Route path="/leave-policy" element={<LeavePolicyManagement />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_ALLOCATIONS} />}>
                            <Route path="/user-leave-allocations" element={<UserLeaveAllocationManagement />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_BALANCES} />}>
                            <Route path="/all-user-leave-balances" element={<AllUserLeaveBalances />} />
                          </Route>

                          {/* Shifts — Institution Admin (gated by sub-menu permission) */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_LIST} />}>
                            <Route path="/shifts" element={<ShiftListPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_ASSIGNMENTS} />}>
                            <Route path="/shifts/assignments" element={<ShiftAssignmentPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_ROTATION_PATTERNS} />}>
                            <Route path="/shifts/rotation-patterns" element={<RotationPatternListPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_ROSTER} />}>
                            <Route path="/shifts/roster" element={<ShiftRosterCalendar />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_ORPHANS} />}>
                            <Route path="/shifts/orphan-punches" element={<OrphanPunchInbox />} />
                          </Route>

                          {/* Payroll Routes - Institution Admin and System Admin */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_PAYROLL} />}>
                            <Route path="/payroll/salary-components" element={<SalaryComponentList />} />
                            <Route path="/payroll/wage-grades" element={<WageGradeManagement />} />
                            <Route path="/payroll/increments" element={<AnnualIncrements />} />
                            <Route path="/payroll/employee-salaries" element={<EmployeeSalaryList />} />
                            <Route path="/payroll/settings" element={<PayrollSettings />} />
                            <Route path="/payroll/run" element={<PayrollList />} />
                            <Route path="/payroll/run/:id" element={<PayrollDetail />} />
                            <Route path="/payroll/reconciliation" element={<PaymentReconciliation />} />
                            <Route path="/payroll/festival-bonus" element={<FestivalBonusList />} />
                            <Route path="/payroll/leave-encashment" element={<LeaveEncashmentList />} />
                            {/* W3-D employee loans: the controller guards with PAYROLL_VIEW/PAYROLL_MANAGE */}
                            <Route path="/payroll/loans" element={<EmployeeLoans />} />
                            <Route path="/payroll/loan-batches" element={<LoanDisbursementBatches />} />
                            {/* W3-J statutory provisions and provident fund: controllers guard with PAYROLL_VIEW/PAYROLL_MANAGE */}
                            <Route path="/payroll/provisions" element={<PayrollProvisions />} />
                            <Route path="/payroll/provident-fund" element={<ProvidentFund />} />
                            {/* Final-review Finding 4: EmployeeExitController's list route is guarded by
                                USER_VIEW, not MENU_PAYROLL/PAYROLL_VIEW (same M6 reasoning as the
                                'payroll.exits' nav gate in useModuleVisibility.ts). Before this fix the
                                ROUTE itself only required MENU_PAYROLL, so a role holding MENU_PAYROLL but
                                not USER_VIEW could reach this page by typed URL/bookmark/back-navigation and
                                have it 403 on load -- nested here so the route requires BOTH keys, matching
                                the nav entry that was already gated correctly. */}
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.USER_VIEW} />}>
                              <Route path="/payroll/exits" element={<EmployeeExitList />} />
                            </Route>
                          </Route>
                          {/* I6: /payroll/batches is split out of the group above because the payment-batch
                              / bank-advice screen has its own key. Merge (develop -> dynamic roles): the
                              route used to be MENU_PAYROLL + an allowedRoles fallback of
                              {InstitutionAdmin, SystemAdmin, Accountant}; `allowedRoles` no longer exists
                              because the global role enum is gone. It is now keyed to
                              SALARY_PAYMENT_MANAGE, which is exactly what the backend
                              SalaryPaymentController (incl. develop's bank-advice CSV handler) asserts. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALARY_PAYMENT_MANAGE} />}>
                            <Route path="/payroll/batches" element={<PaymentBatchList />} />
                          </Route>

                          {/* T3a (piece-rate garments completion plan, 2026-09-09): own keys, not
                              MENU_PAYROLL -- mirrors the backend controller guards
                              (PieceRateOperationController#getByInstitution,
                              ProductionEntryController#listForUser). */}
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.PIECE_RATE_VIEW, PERMISSIONS.PIECE_RATE_MANAGE, PERMISSIONS.PRODUCTION_ENTRY_RECORD, PERMISSIONS.PRODUCTION_ENTRY_APPROVE]} />}>
                            <Route path="/payroll/piece-rate-operations" element={<PieceRateOperationList />} />
                          </Route>
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.PIECE_RATE_VIEW, PERMISSIONS.PRODUCTION_ENTRY_RECORD, PERMISSIONS.PRODUCTION_ENTRY_APPROVE]} />}>
                            <Route path="/payroll/production-entries" element={<ProductionEntryList />} />
                          </Route>

                          {/* Tax Module Routes - Institution Admin and Accountant */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_TAX} />}>
                            <Route path="/tax/settings" element={<TaxSettings />} />
                            <Route path="/tax/slabs" element={<TaxSlabList />} />
                            <Route path="/tax/exemptions" element={<TaxExemptionList />} />
                            <Route path="/tax/employees" element={<EmployeeTaxList />} />
                            <Route path="/tax/reports" element={<TaxReports />} />
                          </Route>

                          {/* Group insurance (BLA §99) — R6: gated on INSURANCE_VIEW, a NEW UNGATED
                              module, deliberately NOT MENU_PAYROLL, so a payroll-disabled tenant
                              keeps §99 compliance reachable. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.INSURANCE_VIEW} />}>
                            <Route path="/insurance/policy" element={<InsurancePolicyPanel />} />
                          </Route>

                          {/* Commercial (T9) — LC_VIEW for reads, LC_MANAGE for writes, matching
                              @authz.can guards on ExportLcController/BackToBackLcController exactly.
                              Module gating (R-1, gatedModule: 'commercial') is a separate, earlier
                              seam handled by useModuleVisibility/moduleCatalog for the launcher and
                              sidebar; this permission guard is the route-level access check. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.LC_VIEW} />}>
                            <Route path="/commercial/lc" element={<ExportLcList />} />
                            <Route path="/commercial/lc/:id" element={<ExportLcDetail />} />
                            <Route path="/commercial/back-to-back-lc" element={<BackToBackLcRegister />} />
                            <Route path="/commercial/back-to-back-lc/:masterLcId/:b2bId" element={<B2bLcDetail />} />
                            {/* Commercial party master (Task 4a, spec §2, 7.4) -- CommercialPartyController's
                                @authz.can guards reuse LC_VIEW/LC_MANAGE, no dedicated permission catalog
                                entry. Create/edit is an in-page dialog gated on LC_MANAGE inside
                                CommercialPartyList itself, matching BondLicenseList's pattern. */}
                            <Route path="/commercial/parties" element={<CommercialPartyList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.LC_MANAGE} />}>
                            <Route path="/commercial/lc/new" element={<ExportLcForm />} />
                            <Route path="/commercial/lc/:id/edit" element={<ExportLcForm />} />
                          </Route>
                          {/* Maintenance (L0): GATED module (`gatedModule: 'maintenance'` in moduleCatalog.ts);
                              these guards mirror the controllers' @authz keys. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MAINTENANCE_EQUIPMENT_VIEW} />}>
                            <Route path="/maintenance/equipment" element={<EquipmentList />} />
                            <Route path="/maintenance/equipment/:id" element={<EquipmentDetail />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MAINTENANCE_EQUIPMENT_MANAGE} />}>
                            <Route path="/maintenance/equipment/new" element={<EquipmentForm />} />
                            <Route path="/maintenance/equipment/import" element={<EquipmentImport />} />
                            <Route path="/maintenance/equipment/:id/edit" element={<EquipmentForm />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MAINTENANCE_READING_RECORD} />}>
                            <Route path="/maintenance/readings" element={<UtilityLogSheet />} />
                          </Route>
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.MAINTENANCE_READING_RECORD, PERMISSIONS.MAINTENANCE_REPORT_VIEW]} />}>
                            <Route path="/maintenance/readings/:meterId" element={<MeterReadingHistory />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MAINTENANCE_SETTINGS_MANAGE} />}>
                            <Route path="/maintenance/settings" element={<MaintenanceSettings />} />
                          </Route>
                          {/* Maintenance (L1): triage inbox and work orders. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MAINTENANCE_REQUEST_TRIAGE} />}>
                            <Route path="/maintenance/requests" element={<BreakdownRequestInbox />} />
                          </Route>
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.MAINTENANCE_WO_VIEW_ALL, PERMISSIONS.MAINTENANCE_WO_WORK, PERMISSIONS.MAINTENANCE_WO_MANAGE]} />}>
                            <Route path="/maintenance/work-orders" element={<WorkOrderList />} />
                            <Route path="/maintenance/work-orders/:id" element={<WorkOrderDetail />} />
                          </Route>
                          {/* Maintenance (L4): the manager board and the technician's own board. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MAINTENANCE_WO_VIEW_ALL} />}>
                            <Route path="/maintenance/dashboard" element={<MaintenanceDashboard />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MAINTENANCE_WO_WORK} />}>
                            <Route path="/maintenance/my-work" element={<TechnicianDashboard />} />
                          </Route>
                          {/* Maintenance (L3): preventive maintenance plans and the due list. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MAINTENANCE_PM_VIEW} />}>
                            <Route path="/maintenance/pm-plans" element={<PmPlanList />} />
                            <Route path="/maintenance/pm-plans/:id" element={<PmPlanForm />} />
                            <Route path="/maintenance/pm-due" element={<PmDueList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MAINTENANCE_PM_MANAGE} />}>
                            <Route path="/maintenance/pm-plans/new" element={<PmPlanForm />} />
                          </Route>
                          {/* Maintenance (L5): reports. The page lists only the reports the caller may open; the utility
                              ones are also open to a meter reader (READING_RECORD), mirroring the controller. */}
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.MAINTENANCE_REPORT_VIEW, PERMISSIONS.MAINTENANCE_READING_RECORD]} />}>
                            <Route path="/maintenance/reports" element={<MaintenanceReports />} />
                            <Route path="/maintenance/reports/:report" element={<MaintenanceReports />} />
                          </Route>
                          {/* Commercial / customs (T10) -- UD, EXP and cash-incentive screens. LC/B2B
                              routes belong to T9's LC screens on a parallel branch and are wired
                              separately; the module is GATED (R-1) via `gatedModule: 'commercial'`
                              in moduleCatalog.ts, so these routes are additionally invisible on the
                              launcher/sidebar for any tenant that has not enabled the module -- the
                              PermissionRoute guard below is defence in depth, matching every other
                              module in this file. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.UD_VIEW} />}>
                            <Route path="/commercial/ud-register" element={<UdRegisterList />} />
                            <Route path="/commercial/ud-register/:id" element={<UdRegisterDetail />} />
                            {/* Bond-warehouse license CRUD (T8, brief item 13.1) --
                                BondLicenseController's @authz.can guards reuse UD_VIEW/UD_MANAGE
                                (no dedicated BOND_LICENSE key), so this route lives in the same
                                PermissionRoute block; create/edit is an in-page dialog gated on
                                UD_MANAGE inside BondLicenseList itself. */}
                            <Route path="/commercial/bond-license" element={<BondLicenseList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.UD_MANAGE} />}>
                            <Route path="/commercial/ud-register/new" element={<UdRegisterForm />} />
                            <Route path="/commercial/ud-register/:id/edit" element={<UdRegisterForm />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXP_VIEW} />}>
                            <Route path="/commercial/exp-form" element={<ExpFormList />} />
                            <Route path="/commercial/exp-form/:id" element={<ExpFormForm />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXP_MANAGE} />}>
                            <Route path="/commercial/exp-form/new" element={<ExpFormForm />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.CASH_INCENTIVE_VIEW} />}>
                            <Route path="/commercial/cash-incentive" element={<CashIncentiveClaimList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.CASH_INCENTIVE_MANAGE} />}>
                            <Route path="/commercial/cash-incentive/new" element={<CashIncentiveClaimForm />} />
                          </Route>
                          {/* W3-W UD reconciliation */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.UD_VIEW} />}>
                            <Route path="/commercial/ud-reconciliation" element={<UdReconciliation />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.AGENT_COMMISSION_VIEW} />}>
                            <Route path="/commercial/agent-commission" element={<AgentCommissionPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.COMMERCIAL_SETTINGS_VIEW} />}>
                            <Route path="/commercial/settings" element={<CommercialSettingsPage />} />
                          </Route>
                          {/* Commercial reports (T11) -- read-only, share COMMERCIAL_REPORT_VIEW,
                              matching @authz.can('COMMERCIAL_REPORT_VIEW', ...) on
                              CommercialReportController exactly. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.COMMERCIAL_REPORT_VIEW} />}>
                            <Route path="/commercial/dashboard" element={<CommercialDashboard />} />
                            <Route path="/commercial/reports/lc-position" element={<LcPositionReport />} />
                            <Route path="/commercial/reports/b2b-maturity" element={<B2bMaturityScheduleReport />} />
                            <Route path="/commercial/reports/unrealized-exp-aging" element={<UnrealizedExpAgingReport />} />
                            <Route path="/commercial/reports/export-receivables" element={<ExportReceivablesReport />} />
                            <Route path="/commercial/reports/incentive-claim-status" element={<IncentiveClaimStatusReport />} />
                            {/* Bond register (T8, brief item 13.3) -- shares COMMERCIAL_REPORT_VIEW,
                                matching CommercialReportController's bond-register routes. */}
                            <Route path="/commercial/reports/bond-register" element={<BondRegisterReport />} />
                            {/* Order profitability (T6, brief item 16.3) -- shares COMMERCIAL_REPORT_VIEW,
                                matching CommercialReportController's order-profitability routes. */}
                            <Route path="/commercial/reports/order-profitability" element={<OrderProfitabilityReport />} />
                            {/* W3-W realisation performance */}
                            <Route path="/commercial/reports/realisation-performance" element={<RealisationPerformanceReport />} />
                          </Route>
                          {/* Shipping & export docs (15.2/15.3) -- packing list (header + carton
                              lines) and bill of lading / forwarder. Reuses EXP_VIEW/EXP_MANAGE
                              (spec §3, no new permission catalog entry), matching @authz.can on
                              PackingListController/BillOfLadingController exactly. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXP_VIEW} />}>
                            {/* W3-W overdue export bills */}
                            <Route path="/commercial/overdue-exp" element={<OverdueExpBills />} />
                            <Route path="/commercial/packing-lists" element={<PackingListList />} />
                            <Route path="/commercial/packing-lists/:id" element={<PackingListDetail />} />
                            <Route path="/commercial/bills-of-lading" element={<BillOfLadingList />} />
                            <Route path="/commercial/bills-of-lading/:id" element={<BillOfLadingDetail />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXP_MANAGE} />}>
                            <Route path="/commercial/packing-lists/new" element={<PackingListForm />} />
                            <Route path="/commercial/packing-lists/:id/edit" element={<PackingListForm />} />
                            <Route path="/commercial/bills-of-lading/new" element={<BillOfLadingForm />} />
                            <Route path="/commercial/bills-of-lading/:id/edit" element={<BillOfLadingForm />} />
                          </Route>
                          {/* Export invoice (15.1, plan task 7) -- header + line rows (replace-all),
                              PDF download. Reuses EXP_VIEW/EXP_MANAGE (spec §3, no new permission
                              catalog entry), matching @authz.can on ExportInvoiceController exactly. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXP_VIEW} />}>
                            <Route path="/commercial/export-invoices" element={<ExportInvoiceList />} />
                            <Route path="/commercial/export-invoices/:id" element={<ExportInvoiceDetail />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXP_MANAGE} />}>
                            <Route path="/commercial/export-invoices/new" element={<ExportInvoiceForm />} />
                            <Route path="/commercial/export-invoices/:id/edit" element={<ExportInvoiceForm />} />
                          </Route>

                          {/* System Admin Only Routes */}
                          <Route element={<SystemAdminRoute />}>
                            <Route path="/institutions" element={<InstitutionList />} />
                            <Route path="/institutions/create" element={<CreateInstitution />} />
                            <Route path="/institutions/:id" element={<InstitutionDetail />} />
                            <Route path="/all-institution-users" element={<AllInstitutionUsers />} />
                            <Route path="/app-institution-stores" element={<AllInstitutionStoreList />} />
                            <Route path="/platform/missing-help" element={<MissingHelpPage />} />
                          </Route>

                          {/* SaaS Operator console — premium dashboard provisioning across institutions */}
                          <Route element={<PermissionRoute platformRoles={[UserType.SaaSOperator, UserType.SystemAdmin]} />}>
                            <Route path="/saas/premium-provisioning" element={<TenantProvisioning />} />
                            <Route path="/saas/usage" element={<PlatformUsagePage />} />
                            <Route path="/saas/institution-groups" element={<InstitutionGroups />} />{/* W3-M */}
                            <Route path="/platform/feedback" element={<PlatformFeedbackPage />} />
                            <Route path="/platform/addons" element={<PlatformAddonsPage />} />
                          </Route>

                          {/* Institution Admin can manage their own institution's settings (e.g. weekend days) */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.INSTITUTION_SETTINGS_VIEW} />}>
                            <Route path="/institution-settings" element={<InstitutionSettingsPage />} />
                          </Route>

                          {/* W3-S notifications: own inbox and preferences for every member; rules for settings managers */}
                          <Route path="/notifications" element={<NotificationsPage />} />
                          <Route path="/settings/notifications" element={<NotificationPreferencesPage />} />
                          {/* ---- FW-3A approvals: the inbox is for every member (the server scopes it); workflows need APPROVAL_WORKFLOW_MANAGE ---- */}
                          <Route path="/approvals" element={<ApprovalsInbox />} />
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.APPROVAL_WORKFLOW_MANAGE} />}>
                            <Route path="/settings/approval-workflows" element={<ApprovalWorkflows />} />
                          </Route>
                          {/* ---- end FW-3A ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.INSTITUTION_SETTINGS_MANAGE} />}>
                            <Route path="/settings/reminder-rules" element={<ReminderRulesPage />} />
                          </Route>

                          {/* Other existing routes */}
                          <Route path="/attendance" element={<AttendanceList />} />
                          <Route path="/holidays" element={<HolidayList />} />
                          <Route path="/leave-applications" element={<LeaveApplicationList />} />

                          {/* Store Admin and Institution Admin Only Routes */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_STORES} />}>
                            <Route path="/item-categories" element={<ItemCategoryList />} />
                            <Route path="/items" element={<ItemList />} />
                            <Route path="/stock-in" element={<StockIn />} />
                            <Route path="/stock-out" element={<StockOut />} />
                            <Route path="/stock-history" element={<StockHistory />} />
                            <Route path="/unit-of-measurement" element={<UnitOfMeasurementList />} />
                          </Route>
                          {/* B-28: the stock list API is STOCK_VIEW; Store Staff hold it without MENU_STORES */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_VIEW} showNoAccess />}>
                            <Route path="/stocks" element={<StockList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_MANAGE} />}>
                            <Route path="/low-stock" element={<LowStockList />} />
                            <Route path="/fabric-lots" element={<FabricLotRegister />} />
                            <Route path="/stocktakes" element={<StocktakeList />} />
                            <Route path="/stocktakes/create" element={<StocktakeCreate />} />
                            <Route path="/stocktakes/:id" element={<StocktakeDetails />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STOCK_MANAGE} />}>
                            <Route path="/stock-transfers" element={<StockTransferList />} />
                            <Route path="/stock-transfers/create" element={<StockTransferCreate />} />
                            <Route path="/stock-transfers/:id" element={<StockTransferDetails />} />
                          </Route>

                          {/* Employee self-service: any logged-in institution member; the API scopes to the caller */}
                          <Route path="/my-payslips" element={<MyPayslips />} />
                          <Route path="/q/:type/:id" element={<QrLanding />} />{/* Q1: printed QR labels land here */}
                          <Route path="/my-loans" element={<MyLoans />} />{/* W3-D */}
                          <Route path="/my/petty-cash" element={<MyPettyCash />} />{/* W3-Q custodian self-service */}
                          <Route path="/my/breakdown-requests" element={<MyBreakdownSection />} />{/* Maintenance L1: any employee */}
                          <Route path="/my-provident-fund" element={<MyProvidentFund />} />{/* W3-J */}
                          <Route path="/my-requisitions" element={<MyRequisitions />} />
                          <Route path="/requisition-create" element={<RequisitionCreate />} />
                          <Route path="/requisition/:id" element={<RequisitionDetails />} />

                          {/* User Profile Route - Available for all authenticated users */}
                          <Route path="/profile" element={<UserProfile />} />
                          <Route path="/profile/feedback" element={<MyFeedbackPage />} />

                          {/* Routes only for Institution Admin, Store Admin, and Store Staff */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.REQUISITION_ISSUE} />}>
                            <Route path="/pending-issue-requisitions" element={<PendingIssueRequisitions />} />
                            <Route path="/pending-issue-requisitions/:id" element={<PendingIssueRequisitionDetails />} />
                          </Route>
                          {/* Approval pages need REQUISITION_APPROVE (approve/reject); the pending-approval list also accepts it. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.REQUISITION_APPROVE} />}>
                            <Route path="/pending-approval-requisitions" element={<PendingApprovalRequisitions />} />
                            <Route path="/pending-approval-requisitions/:id" element={<PendingApprovalRequisitionDetails />} />
                          </Route>

                          {/* Accounting Routes - Accountant and Institution Admin */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_ACCOUNTING} />}>
                            <Route path="/accounting/chart-of-accounts" element={<ChartOfAccounts />} />
                            <Route path="/accounting/transactions" element={<TransactionList />} />
                            <Route path="/accounting/journal-entries" element={<JournalEntryList />} />
                            <Route path="/accounting/journal-entries/create" element={<JournalEntryCreate />} />
                            <Route path="/accounting/journal-entries/contra" element={<ContraVoucherCreate />} />
                            <Route path="/accounting/journal-entries/:id" element={<JournalEntryDetail />} />
                            <Route path="/accounting/recurring-journals" element={<RecurringJournalList />} />
                            <Route path="/accounting/recurring-journals/new" element={<RecurringJournalForm />} />
                            <Route path="/accounting/recurring-journals/:id/edit" element={<RecurringJournalForm />} />
                            <Route path="/accounting/recurring-journals/:id" element={<RecurringJournalDetail />} />
                            <Route path="/accounting/budgets" element={<BudgetList />} />
                            <Route path="/accounting/accounts/:id/ledger" element={<AccountLedger />} />
                            <Route path="/accounting/trial-balance" element={<TrialBalance />} />
                            <Route path="/accounting/segments" element={<SegmentList />} />
                            <Route path="/accounting/segment-settings" element={<SegmentSettings />} />
                            {/* W3-C inter-segment loans */}
                            <Route path="/accounting/inter-segment/loans" element={<InterSegmentLoans />} />
                            <Route path="/accounting/inter-segment/settings" element={<InterSegmentSettings />} />
                            <Route path="/accounting/inter-segment/reconciliation" element={<InterSegmentReconciliation />} />
                            <Route path="/accounting/inter-segment/elimination" element={<InterSegmentElimination />} />
                            <Route path="/accounting/ledger-close-settings" element={<LedgerCloseSettings />} />
                            <Route path="/accounting/year-end-close" element={<YearEndClose />} />
                            {/* W3-R compliance */}
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.COMPLIANCE_VIEW} />}>
                              <Route path="/accounting/compliance-calendar" element={<ComplianceCalendar />} />
                              <Route path="/accounting/close-checklist" element={<CloseChecklist />} />
                            </Route>
                            <Route element={<PermissionRoute anyOf={[PERMISSIONS.TAX_VIEW, PERMISSIONS.PAYROLL_VIEW]} />}>
                              <Route path="/payroll/statutory-remittances" element={<StatutoryRemittances />} />
                            </Route>
                            <Route path="/accounting/allocation-rules" element={<AllocationRuleList />} />
                            <Route path="/accounting/segment-trial-balance" element={<SegmentTrialBalance />} />
                            <Route path="/accounting/income-statement" element={<SegmentIncomeStatement />} />
                            <Route path="/accounting/balance-sheet" element={<SegmentBalanceSheet />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.JOURNAL_ENTRY_MANAGE} />}>
                            <Route path="/accounting/group-balances" element={<GroupAccountBalances />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.INDIRECT_TAX_VIEW} />}>
                              <Route path="/accounting/tax-codes" element={<TaxCodesPage />} />
                              <Route path="/accounting/vat-return" element={<VatReturnPage />} />
                              <Route path="/accounting/mushak/purchase-register" element={<MushakRegisterPage form="6.1" />} />
                              <Route path="/accounting/mushak/sales-register" element={<MushakRegisterPage form="6.2" />} />
                              <Route path="/accounting/mushak/return-9-1" element={<Mushak91Page />} />
                              {/* ---- B4-A ---- */}
                              <Route path="/accounting/mushak/statement-6-10" element={<Mushak610Page />} />
                              <Route path="/accounting/mushak/coefficients" element={<MushakCoefficientPage />} />
                              {/* ---- end B4-A ---- */}
                            </Route>
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.ACCOUNT_MANAGE} />}>
                            <Route path="/accounting/opening-balances" element={<OpeningBalances />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.ACCOUNT_VIEW} />}>
                            <Route path="/accounting/exchange-rates" element={<ExchangeRates />} />
                            <Route path="/accounting/fx-revaluation" element={<FxRevaluation />} />
                          </Route>
                          {/* ---- W3-V FP&A: each backend call re-checks its own key ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.BUDGET_VIEW} />}>
                            <Route path="/accounting/budget-versions" element={<BudgetVersions />} />
                            <Route path="/accounting/variance" element={<BudgetVarianceReview />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.FINANCIAL_REPORT_VIEW} />}>
                            <Route path="/accounting/kpis" element={<KpiPack />} />
                            <Route path="/accounting/board-pack" element={<BoardPackPage />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.COSTING_VIEW} />}>
                              <Route path="/accounting/buyer-profitability" element={<BuyerProfitabilityPage />} />
                            </Route>
                          </Route>
                          {/* ---- end W3-V ---- */}
                          {/* ---- W3-X data in/out: each import kind and every integration call re-checks its own key ---- */}
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.CUSTOMER_MANAGE, PERMISSIONS.SUPPLIER_MANAGE, PERMISSIONS.ITEM_MANAGE, PERMISSIONS.ACCOUNT_MANAGE, PERMISSIONS.TRANSACTION_MANAGE]} />}>
                            <Route path="/data-import" element={<DataImport />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.INTEGRATION_MANAGE} />}>
                            <Route path="/integrations" element={<Integrations />} />
                            <Route path="/integrations/xero/callback" element={<XeroCallbackPage />} />
                          </Route>
                          {/* ---- end W3-X ---- */}
                          {/* ---- W3-M inter-company: INTERCOMPANY_VIEW; each backend call re-checks the key per institution ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.INTERCOMPANY_VIEW} />}>
                            <Route path="/accounting/intercompany" element={<Intercompany />} />
                            <Route path="/accounting/group-consolidation" element={<GroupConsolidation />} />
                          </Route>
                          {/* ---- end W3-M ---- */}
                          {/* W3-G corporate tax */}
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.ACCOUNT_VIEW, PERMISSIONS.FINANCIAL_REPORT_VIEW]} />}>
                            <Route path="/accounting/corporate-tax" element={<CorporateTaxPage />} />
                          </Route>

                          {/* Banking (T10, bank-rec close-out) -- BankReconciliationWorkspace reads its
                              route param as `id` (useParams<{ id: string }>()), so the workspace route
                              below uses :id, not :recId, to match. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.BANK_ACCOUNT_VIEW} />}>
                            <Route path="/banking/accounts" element={<BankAccountList />} />
                            <Route path="/banking/accounts/new" element={<BankAccountForm />} />
                            <Route path="/banking/accounts/:id/edit" element={<BankAccountForm />} />
                            <Route path="/banking/accounts/:id" element={<BankReconciliationList />} />
                            <Route path="/banking/reconciliations/:id" element={<BankReconciliationWorkspace />} />
                            <Route path="/banking/unreconciled" element={<UnreconciledReport />} />
                            {/* W3-H */}
                            <Route path="/banking/cheques" element={<ChequeRegister />} />
                            <Route path="/banking/cheque-books" element={<ChequeBooks />} />
                            <Route path="/banking/cheque-layouts" element={<ChequeLayouts />} />
                            <Route path="/banking/guarantees" element={<BankGuarantees />} />
                            {/* W3-Q */}
                            <Route path="/banking/cash-position" element={<CashPosition />} />
                            <Route path="/banking/transfers" element={<BankTransfers />} />
                            <Route path="/banking/fdrs" element={<Fdrs />} />
                            <Route path="/banking/petty-cash" element={<PettyCash />} />
                            <Route path="/banking/treasury-settings" element={<TreasurySettings />} />
                            {/* W3-L 13-week cash forecast: same BANK_ACCOUNT_VIEW gate as the rest of banking */}
                            <Route path="/banking/cash-forecast" element={<CashForecast />} />
                            {/* ---- B4-B FC bank and ERQ ---- */}
                            <Route path="/banking/fc-conversions" element={<FcConversions />} />
                            {/* ---- end B4-B ---- */}
                            {/* ---- FW-4A bank rules (BANK_ACCOUNT_VIEW to read; edit buttons need BANK_RULE_MANAGE) ---- */}
                            <Route path="/banking/rules" element={<BankRules />} />
                            {/* ---- end FW-4A ---- */}
                          </Route>
                          {/* ---- F11 cash desk: per-cashbook access (manage key) and transaction mapping rules (TRANSACTION_MANAGE to read) ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.CASHBOOK_ACCESS_MANAGE} />}>
                            <Route path="/banking/cashbook-access" element={<CashbookAccess />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.TRANSACTION_MANAGE} />}>
                            <Route path="/accounting/transaction-rules" element={<TxnMappingRules />} />
                            {/* ---- end F11 ---- */}
                          </Route>

                          {/* W3-I Costing */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.COSTING_VIEW} />}>
                            <Route path="/costing" element={<OrderCosts />} />
                            <Route path="/costing/orders" element={<OrderCosts />} />
                            <Route path="/costing/orders/:orderId" element={<OrderCostDetail />} />
                            <Route path="/costing/output" element={<ProductionOutputPage />} />
                            <Route path="/costing/absorption" element={<AbsorptionRuns />} />
                            <Route path="/costing/settings" element={<CostingSettingsPage />} />
                          </Route>

                          {/* W3-A Financing */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.FINANCING_VIEW} />}>
                            <Route path="/financing" element={<FacilityList />} />
                            <Route path="/financing/facilities/:id" element={<FacilityDetail />} />
                            <Route path="/financing/interest" element={<InterestRuns />} />
                            <Route path="/financing/reports" element={<BorrowingReports />} />
                            <Route path="/financing/covenants" element={<Covenants />} />
                            {/* W3-B */}
                            <Route path="/financing/export-finance" element={<ExportFinance />} />
                          </Route>

                          {/* Fixed Assets */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.FIXED_ASSET_VIEW} />}>
                           <Route element={<MenuVisibilityRoute visibilityKey="fixedAssets" />}>
                            <Route path="/fixed-assets" element={<FixedAssetList />} />
                            <Route path="/fixed-assets/new" element={<FixedAssetForm />} />
                            <Route path="/fixed-assets/register" element={<FixedAssetForm />} />
                            <Route path="/fixed-assets/depreciation" element={<DepreciationRun />} />
                            {/* GL account defaults = accounting setup: accounts team only */}
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.FIXED_ASSET_SETTINGS_MANAGE} />}>
                              <Route path="/fixed-assets/settings" element={<FixedAssetSettings />} />
                            </Route>
                            <Route path="/fixed-assets/reports/register" element={<AssetRegisterReport />} />
                            <Route path="/fixed-assets/reports/schedule" element={<DepreciationScheduleReport />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.FINANCIAL_REPORT_VIEW} />}>
                              <Route path="/fixed-assets/reports/tie-out" element={<FixedAssetTieOutReport />} />
                            </Route>
                            {/* ---- W3-K asset categories and CWIP ---- */}
                            <Route path="/fixed-assets/categories" element={<FixedAssetCategories />} />
                            <Route path="/fixed-assets/cwip" element={<CwipProjects />} />
                            {/* ---- end W3-K asset categories and CWIP ---- */}
                            {/* ---- F10 asset locations ---- */}
                            <Route path="/fixed-assets/locations" element={<FixedAssetLocations />} />
                            {/* ---- end F10 ---- */}
                            <Route path="/fixed-assets/counts" element={<FixedAssetCounts />} />{/* Q1 */}
                            <Route path="/fixed-assets/counts/:id" element={<FixedAssetCountDetail />} />{/* Q1 */}
                            <Route path="/fixed-assets/:id" element={<FixedAssetDetail />} />
                            <Route path="/fixed-assets/:id/edit" element={<FixedAssetForm />} />
                            <Route path="/fixed-assets/:id/schedule" element={<AssetDepreciationSchedule />} />
                           </Route>
                          </Route>

                          {/* ---- W3-K journal-import/tally ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.JOURNAL_ENTRY_MANAGE} />}>
                            <Route path="/accounting/journal-import" element={<JournalImport />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.FINANCIAL_REPORT_VIEW} />}>
                            <Route path="/accounting/tally-export" element={<TallyExport />} />
                          </Route>
                          {/* ---- end W3-K journal-import/tally ---- */}
                          {/* Inventory vs Ledger tie-out: same ACCOUNT_VIEW key as the financial statements */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.ACCOUNT_VIEW} />}>
                            <Route path="/accounting/cash-flow" element={<CashFlowStatement />} />
                            {/* ---- W3-K statements (ACCOUNT_VIEW from the parent route + FINANCIAL_REPORT_VIEW) ---- */}
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.FINANCIAL_REPORT_VIEW} />}>
                              <Route path="/accounting/equity-statement" element={<EquityStatement />} />
                              <Route path="/accounting/notes" element={<NotesPack />} />
                              {/* ---- B4-H department P&L (same FINANCIAL_REPORT_VIEW key) ---- */}
                              <Route path="/accounting/department-pl" element={<DepartmentPlPage />} />
                              {/* ---- end B4-H ---- */}
                            </Route>
                            {/* ---- end W3-K statements ---- */}
                            <Route path="/accounting/day-book" element={<DayBook />} />
                            <Route path="/accounting/cash-flow-classification" element={<CashFlowClassification />} />
                            <Route path="/inventory/reports/tie-out" element={<InventoryLedgerTieOut />} />
                          </Route>

                          {/* Notices */}
                          <Route path="/notices" element={<NoticeFeed />} />
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.NOTICE_MANAGE} />}>
                            <Route path="/admin/notices" element={<NoticeAdminList />} />
                          </Route>

                          {/* Tasks */}
                          <Route path="/tasks" element={<MyTasks />} />
                          {/* F8: every member may request an asset; the page hides staff-only actions, the API enforces them */}
                          <Route path="/fixed-assets/requisitions" element={<AssetRequisitions />} />
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.TASK_ASSIGN} />}>
                            <Route path="/tasks/assigned-by-me" element={<TasksAssignedByMe />} />
                            <Route path="/admin/tasks" element={<TaskAdminList />} />
                            <Route path="/accounting/audit-log" element={<AuditLog />} /> {/* W3-K */}
                          </Route>
                          {/* ---- P1-A projects: register and P&L need PROJECT_VIEW; detail and "mine" are server-filtered (manager sees own) ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.PROJECT_VIEW} />}>
                            <Route path="/accounting/projects" element={<ProjectList />} />
                            <Route path="/accounting/project-pl" element={<ProjectPlPage />} />
                          </Route>
                          <Route path="/accounting/projects/:id" element={<ProjectDetail />} />
                          <Route path="/projects/mine" element={<MyProjects />} />
                          {/* ---- end P1-A ---- */}

                          {/* Procurement Routes — previously unguarded */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.PURCHASE_ORDER_VIEW} />}>
                            <Route path="/procurement/purchase-orders" element={<PurchaseOrderList />} />
                            <Route path="/procurement/purchase-orders/new" element={<PurchaseOrderCreate />} />
                            <Route path="/procurement/purchase-orders/pending-approval" element={<PendingApprovalPurchaseOrders />} />
                            <Route path="/procurement/purchase-orders/pending-approval/:id" element={<PendingApprovalPurchaseOrderDetail />} />
                            <Route path="/procurement/purchase-orders/:id" element={<PurchaseOrderDetail />} />
                            <Route path="/procurement/goods-receipts" element={<GoodsReceiptList />} />
                            <Route path="/procurement/goods-receipts/new" element={<GoodsReceiptCreate />} />
                            <Route path="/procurement/goods-receipts/:id" element={<GoodsReceiptDetail />} />
                            {/* ---- W3-F import landed cost ---- */}
                            <Route path="/procurement/bills-of-entry" element={<BillOfEntryList />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.GOODS_RECEIPT_POST} />}>
                              <Route path="/procurement/bills-of-entry/new" element={<BillOfEntryWizard />} />
                              <Route path="/procurement/bills-of-entry/:id/edit" element={<BillOfEntryWizard />} />
                            </Route>
                            <Route path="/procurement/bills-of-entry/:id" element={<BillOfEntryDetail />} />
                            {/* ---- end W3-F ---- */}
                            <Route path="/procurement/catalogue" element={<SupplierCatalogueBrowse />} />
                          </Route>
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.SUPPLIER_VIEW, PERMISSIONS.SUPPLIER_MANAGE]} />}>
                            <Route element={<MenuVisibilityRoute visibilityKey="procurement.suppliers" />}>
                              <Route path="/procurement/suppliers" element={<SupplierList />} />
                              <Route path="/procurement/suppliers/:id" element={<SupplierDetail />} />
                            </Route>
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUPPLIER_MANAGE} />}>
                            <Route path="/procurement/suppliers/new" element={<SupplierForm />} />
                            <Route path="/procurement/suppliers/:id/edit" element={<SupplierForm />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.PROCUREMENT_SETTINGS_MANAGE} />}>
                            <Route path="/procurement/settings" element={<ProcurementSettings />} />
                            <Route path="/procurement/payment-settings" element={<PaymentSettings />} /> {/* W3-P */}
                          </Route>
                          {/* F8 supplier bill requests inbox */}
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.SUPPLIER_PORTAL_MANAGE, PERMISSIONS.SUPPLIER_BILL_VIEW]} />}>
                            <Route path="/procurement/bill-requests" element={<BillRequestInbox />} />
                          </Route>
                          {/* Maintenance repair bills waiting for approval */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUPPLIER_BILL_CREATE} />}>
                            <Route path="/procurement/repair-bills" element={<RepairBillQueue />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUPPLIER_BILL_VIEW} />}>
                            <Route path="/procurement/supplier-bills" element={<SupplierBillList />} />
                            <Route path="/procurement/supplier-bills/new" element={<SupplierBillCreate />} />
                            {/* ---- FW-2C ---- */}
                            <Route path="/sales/recurring" element={<RecurringDocumentList scope="sales" />} />
                            <Route path="/sales/recurring/:id" element={<RecurringDocumentDetail />} />
                            <Route path="/procurement/recurring-bills" element={<RecurringDocumentList scope="bills" />} />
                            <Route path="/procurement/recurring-bills/:id" element={<RecurringDocumentDetail />} />
                            {/* ---- end FW-2C ---- */}
                            <Route path="/procurement/supplier-bills/new-service" element={<SupplierServiceBillCreate />} /> {/* W3-F */}
                            <Route path="/procurement/supplier-bills/:id" element={<SupplierBillDetail />} />
                            <Route path="/procurement/supplier-bills/:id/edit" element={<SupplierBillEdit />} />
                            <Route path="/procurement/reports" element={<SupplierPayablesReport />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUPPLIER_BILL_VIEW} />}>
                            <Route path="/procurement/debit-notes" element={<DebitNoteList />} />
                            <Route path="/procurement/debit-notes/new" element={<DebitNoteCreate />} />
                            <Route path="/procurement/debit-notes/:id" element={<DebitNoteDetail />} />
                            {/* ---- B4-G ---- */}
                            <Route path="/procurement/statement-reconciliation" element={<SupplierStatementReconPage />} />
                            {/* ---- end B4-G ---- */}
                            {/* ---- B4-F ---- */}
                            <Route path="/procurement/approval-bands" element={<PoApprovalBands />} />
                            {/* ---- end B4-F ---- */}
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUPPLIER_PAYMENT_VIEW} />}>
                            <Route path="/procurement/supplier-advances" element={<SupplierAdvanceList />} />
                            <Route path="/procurement/supplier-payments" element={<SupplierPaymentList />} />
                            <Route path="/procurement/supplier-payments/new" element={<SupplierPaymentCreate />} />
                            <Route path="/procurement/supplier-payments/:id" element={<SupplierPaymentDetail />} />
                            <Route path="/procurement/supplier-payments/:id/edit" element={<SupplierPaymentEdit />} />
                            <Route path="/procurement/withholding" element={<WithholdingSettingsPage />} />
                            <Route path="/procurement/withholding/register" element={<WithholdingRegister />} />
                            <Route path="/procurement/withholding/deposits" element={<WithholdingDeposits />} />
                            {/* W3-P */}
                            <Route path="/procurement/payment-runs" element={<PaymentRunList />} />
                            <Route path="/procurement/payment-runs/new" element={<PaymentRunDetail />} />
                            <Route path="/procurement/payment-runs/:id" element={<PaymentRunDetail />} />
                            <Route path="/procurement/blocked-bills" element={<BlockedBills />} />
                          </Route>

                          {/* Sales Routes — previously unguarded */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.CUSTOMER_VIEW} showNoAccess />}>
                            <Route path="/sales/customers" element={<CustomerList />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.CUSTOMER_CREATE} showNoAccess />}>
                              <Route path="/sales/customers/new" element={<CustomerForm />} />
                            </Route>
                            <Route path="/sales/customers/:id" element={<CustomerDetail />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.CUSTOMER_MANAGE} showNoAccess />}>
                              <Route path="/sales/customers/:id/edit" element={<CustomerForm />} />
                            </Route>
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_SETTINGS_VIEW} showNoAccess />}>
                            <Route path="/sales/settings" element={<SalesSettings />} />
                            <Route path="/sales/email-templates" element={<EmailTemplatesSettings />} /> {/* F7 */}
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_BRANDING_VIEW} showNoAccess />}>
                            <Route path="/sales/branding" element={<InvoiceBrandingSettings />} />
                          </Route>
                          {/* ---- FW-2A ---- */}
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.SALES_INVOICE_VIEW, PERMISSIONS.QUOTATION_VIEW, PERMISSIONS.SALES_ORDER_VIEW]} showNoAccess />}>
                            <Route path="/sales/catalogue" element={<SalesCatalogue />} />
                          </Route>
                          {/* ---- end FW-2A ---- */}
                          {/* ---- FW-2B cash sale ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_INVOICE_VIEW} showNoAccess />}>
                            <Route path="/sales/cash-sales" element={<CashSaleList />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_INVOICE_MANAGE} showNoAccess />}>
                              <Route element={<PermissionRoute permissionKey={PERMISSIONS.RECEIPT_MANAGE} showNoAccess />}>
                                <Route path="/sales/cash-sales/new" element={<CashSaleForm />} />
                              </Route>
                            </Route>
                          </Route>
                          {/* ---- end FW-2B ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_INVOICE_VIEW} showNoAccess />}>
                            <Route path="/sales/invoices" element={<SalesInvoiceList />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_INVOICE_MANAGE} showNoAccess />}>
                              <Route path="/sales/invoices/new" element={<SalesInvoiceCreate />} />
                              <Route path="/sales/invoices/:id/edit" element={<SalesInvoiceEdit />} />
                            </Route>
                            <Route path="/sales/invoices/:id" element={<SalesInvoiceDetail />} />
                            <Route path="/sales/credit-notes" element={<CreditNoteList />} />
                            <Route path="/sales/credit-notes/new" element={<CreditNoteCreate />} />
                            <Route path="/sales/credit-notes/:id" element={<CreditNoteDetail />} />
                            <Route path="/sales/receipts/:id" element={<ReceiptDetail />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.QUOTATION_VIEW} showNoAccess />}>
                            <Route path="/sales/quotations" element={<QuotationList />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.QUOTATION_MANAGE} showNoAccess />}>
                              <Route path="/sales/quotations/new" element={<QuotationCreate />} />
                              <Route path="/sales/quotations/:id/edit" element={<QuotationCreate />} />
                            </Route>
                            <Route path="/sales/quotations/:id" element={<QuotationDetail />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_TEAM_MANAGE} showNoAccess />}>
                            <Route path="/sales/teams" element={<SalesTeams />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_TARGET_VIEW} showNoAccess />}>
                            <Route path="/sales/targets" element={<SalesTargets />} />
                          </Route>
                          {/* ---- W3-O sales orders and delivery challans ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_ORDER_VIEW} showNoAccess />}>
                            <Route path="/sales/orders" element={<SalesOrderList />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_ORDER_MANAGE} showNoAccess />}>
                              <Route path="/sales/orders/new" element={<SalesOrderForm />} />
                              <Route path="/sales/orders/:id/edit" element={<SalesOrderForm />} />
                            </Route>
                            <Route path="/sales/orders/:id" element={<SalesOrderDetail />} />
                            <Route path="/sales/challans" element={<DeliveryChallanList />} />
                            <Route path="/sales/challans/:id" element={<DeliveryChallanDetail />} />
                            {/* ---- F5 ---- */}<Route path="/sales/shipments" element={<ShipmentList />} />
                            <Route path="/sales/back-orders" element={<BackOrders />} />
                            {/* ---- B4-D customer returns ---- */}
                            <Route path="/sales/returns" element={<SalesReturnList />} />
                            <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_RETURN_MANAGE} showNoAccess />}>
                              <Route path="/sales/challans/:challanId/return" element={<SalesReturnForm />} />
                              <Route path="/sales/returns/:id/edit" element={<SalesReturnForm />} />
                            </Route>
                            <Route path="/sales/returns/:id" element={<SalesReturnDetail />} />
                            {/* ---- end B4-D ---- */}
                          </Route>
                          {/* ---- end W3-O ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_REPORT_VIEW} showNoAccess />}>
                            <Route path="/sales/reports/aging" element={<ArAgingReport />} />
                            <Route path="/sales/reports/statement" element={<CustomerStatement />} />
                          </Route>
                          {/* W3-N collections and receipts */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.RECEIPT_VIEW} showNoAccess />}>
                            <Route path="/sales/collections" element={<CollectionsWorklist />} />
                            <Route path="/sales/remittances" element={<CustomerRemittances />} />
                            <Route path="/sales/dunning" element={<DunningLetters />} />
                            <Route path="/sales/payment-links" element={<PaymentLinks />} />
                            <Route path="/sales/gateway-payouts" element={<GatewayPayouts />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SALES_SETTINGS_VIEW} showNoAccess />}>
                            <Route path="/sales/payment-gateway-settings" element={<PaymentGatewaySettings />} />
                          </Route>
                          {/* ---- P1-B customer portal settings ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.PORTAL_MANAGE} showNoAccess />}>
                            <Route path="/sales/portal-settings" element={<PortalSettings />} />
                          </Route>
                          {/* ---- end P1-B ---- */}

                          {/* CRM Routes */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.CRM_VIEW} showNoAccess />}>
                            <Route path="/crm/leads" element={<LeadList />} />
                            <Route path="/crm/leads/:id" element={<LeadDetail />} />
                            <Route path="/crm/pipeline" element={<PipelineBoard />} />
                            <Route path="/crm/opportunities/:id" element={<OpportunityDetail />} />
                          </Route>

                          {/* Merchandising Routes (garments-merchandising T7-T11). Each
                              route's key matches the *_VIEW permission the corresponding backend
                              controller enforces, mirroring the sales/CRM pattern above. */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.BUYER_VIEW} />}>
                            <Route path="/merchandising/buyers" element={<BuyerList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.STYLE_VIEW} />}>
                            <Route path="/merchandising/styles" element={<StyleList />} />
                            <Route path="/merchandising/styles/new" element={<StyleForm />} />
                            <Route path="/merchandising/styles/:id" element={<StyleForm />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SAMPLE_REQUEST_VIEW} />}>
                            <Route path="/merchandising/samples" element={<SampleRequestList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.BUYER_ORDER_VIEW} />}>
                            <Route path="/merchandising/orders" element={<BuyerOrderList />} />
                            <Route path="/merchandising/orders/new" element={<BuyerOrderForm />} />
                            <Route path="/merchandising/orders/:id" element={<BuyerOrderForm />} />
                            <Route path="/merchandising/orders/:id/journey" element={<OrderJourneyPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.QC_INSPECTION_VIEW} />}>
                            <Route path="/merchandising/qc" element={<QcInspectionList />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MERCH_REPORT_VIEW} />}>
                            <Route path="/merchandising/dashboard" element={<MerchandisingDashboard />} />
                            <Route path="/merchandising/reports/order-book" element={<OrderBookReport />} />
                            <Route path="/merchandising/reports/sample-tracker" element={<SampleTrackerReport />} />
                            <Route path="/merchandising/reports/order-dashboard" element={<OrderDashboard />} />
                            <Route path="/merchandising/reports/sample-workload" element={<SampleWorkloadReport />} />
                          </Route>

                          {/* Expense Routes */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXPENSE_CATEGORY_VIEW} />}>
                            <Route path="/expense/categories" element={<ExpenseCategoryManagement />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXPENSE_POLICY_VIEW} />}>
                            <Route path="/expense/policy" element={<ExpensePolicyManagement />} />
                          </Route>
                          {/* ---- W3-U ---- */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXPENSE_POLICY_VIEW} />}>
                            <Route path="/expense/policy-rates" element={<ExpensePolicyRates />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXPENSE_CLAIM_VIEW_ALL} />}>
                            <Route path="/expense/cards" element={<CorporateCards />} />
                            <Route path="/expense/card-statements" element={<CardStatements />} />
                          </Route>
                          <Route path="/expense/my-cards" element={<MyCardTransactions />} />
                          {/* ---- end W3-U ---- */}
                          <Route path="/expense/my-claims" element={<MyExpenseClaims />} />
                          <Route path="/expense/my-claims/new" element={<ExpenseClaimForm />} />
                          <Route path="/expense/my-claims/:id" element={<ExpenseClaimDetail />} />
                          <Route path="/expense/my-claims/:id/edit" element={<ExpenseClaimForm />} />
                          <Route path="/expense/pending-approval" element={<PendingApprovalExpenseClaims />} />
                          {/* Reviewer-side claim view (someone else's claim); the backend authorises the read. */}
                          <Route path="/expense/claims/:id" element={<ExpenseClaimReview />} />
                          <Route path="/expense/my-advances" element={<MyAdvances />} />
                          <Route path="/expense/advances/pending-approval" element={<PendingApprovalAdvances />} />
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.EXPENSE_CLAIM_VIEW_ALL} />}>
                            <Route path="/expense/all-claims" element={<AllExpenseClaims />} />
                          </Route>

                          {/* Dashboard module deep-pages — reached from the Dashboard submenu */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.DASHBOARD_VIEW} />}>
                            <Route path="/dashboard/attendance" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="attendance" moduleScope="attendance" fallbackIds={MODULE_DEFAULT_PRESETS.attendance} title="Attendance" subtitle="Real attendance, last 30–60 days." />} />
                            <Route path="/dashboard/leave" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="leave" moduleScope="leave" fallbackIds={MODULE_DEFAULT_PRESETS.leave} title="Leave" subtitle="Approvals, status and the overdue pending queue." />} />
                            <Route path="/dashboard/people" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="people" moduleScope="people" fallbackIds={MODULE_DEFAULT_PRESETS.people} title="People" subtitle="Workforce overview: who is in today, headcount, leave and trends." />} />
                          </Route>
                          {/* Business module boards: the Accountant reaches them on DASHBOARD_VIEW_ACCOUNTANT (no DASHBOARD_VIEW, which would also open attendance/leave/people) */}
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.DASHBOARD_VIEW_ACCOUNTANT]} />}>
                            <Route path="/dashboard/accounting" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="accounting" moduleScope="accounting" fallbackIds={MODULE_DEFAULT_PRESETS.accounting} title="Accounting" subtitle="Receivables, spend and monthly income vs expense." />} />
                            <Route path="/dashboard/inventory" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="inventory" moduleScope="inventory" fallbackIds={MODULE_DEFAULT_PRESETS.inventory} title="Inventory" subtitle="Stock value, movement and reorder alerts." />} />
                            <Route path="/dashboard/sales" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="sales" moduleScope="sales" fallbackIds={MODULE_DEFAULT_PRESETS.sales} title="Sales" subtitle="Pipeline, revenue and quotation outcomes." />} />
                            <Route path="/dashboard/payroll" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="payroll" moduleScope="payroll" fallbackIds={MODULE_DEFAULT_PRESETS.payroll} title="Payroll" subtitle="Run totals, department split and overtime." />} />
                            <Route path="/dashboard/expense" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="expense" moduleScope="expense" fallbackIds={MODULE_DEFAULT_PRESETS.expense} title="Expense" subtitle="Approval queue, monthly trend and top claimants." />} />
                          </Route>

                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.DASHBOARD_KPI_TARGET_MANAGE} />}>
                            <Route path="/dashboard/settings/targets" element={<KpiTargetsSettings />} />
                          </Route>

                          {/* Managed dashboards (D1): role/department defaults and other people's boards */}
                          <Route element={<PermissionRoute anyOf={[PERMISSIONS.DASHBOARD_LAYOUT_MANAGE_ALL, PERMISSIONS.DASHBOARD_LAYOUT_MANAGE_TEAM]} showNoAccess />}>
                            <Route path="/dashboard/settings/role-defaults" element={<RoleDefaultsPage />} />
                            <Route path="/dashboard/team" element={<TeamDashboardsPage />} />
                            <Route path="/dashboard/team/:userId" element={<PersonBoardPage />} />
                          </Route>

                          <Route path={LANDING_ROUTE} element={<RoleLanding />} />
                          <Route path="/modules" element={<ModulesPage />} />
                          <Route path="/modules/:moduleKey" element={<ModuleDetailPage />} />
                          <Route path="/" element={<Dashboard />} />
                          <Route path="*" element={<PageNotFound />} />
                        </Routes>
                    </AppShell>
                    </HelpProvider>
                  </DashboardAccessProvider>
                  </PlanProvider>
                }
              />
            </Route>
          </Routes>
        </div>
        </NavDrawerContext.Provider>
      </Router>
      </DashboardStyleProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
